import type { Customer360, TicketRecord, UsageLine } from "@/lib/types";

export const MARIA_ID = "00447-190288-15";

export const MARIA_SEARCH_KEYS = [
  "maria",
  "delgado",
  "maria delgado",
  "00447-190288-15",
  "00447",
  "190288-15",
  "4471-902-8815",
  "4471",
  "902-8815",
];

export const MARIA_CONTACT_FULL = {
  mobile: "(312) 555-0184",
  email: "maria.delgado@email.com",
  serviceAddress: "2214 W Belden Ave, Chicago, IL 60647",
  billingAddress: "Same as service address",
  contactPreference: "Text message",
};

export const MARIA_CONTACT_MASKED = {
  mobile: "(•••) •••-0184",
  email: "m•••••o@email.com",
  serviceAddress: "••••  W Belden Ave, Chicago, IL",
  billingAddress: "Same as service address",
  contactPreference: "Text message",
};

export const MARIA_USAGE: UsageLine[] = [
  { line: "(312) 555-0184 · Maria", dataUsed: "18.2 GB", voice: "412 min", roaming: "$28.50 (Mexico)" },
  { line: "(312) 555-0191 · Line 2", dataUsed: "9.7 GB", voice: "156 min", roaming: "—" },
  { line: "(312) 555-0207 · Line 3", dataUsed: "31.4 GB", voice: "88 min", roaming: "—" },
];

export const MARIA_TICKETS: TicketRecord[] = [
  {
    id: "INC0049307",
    summary: "Home internet intermittent outage — node CHI-114",
    priority: "P2",
    status: "In progress",
    assigned: "Network Ops",
    category: "Network / service",
  },
  {
    id: "INC0049311",
    summary: "Billing inquiry — international roaming charges Sep invoice",
    priority: "P3",
    status: "Open",
    assigned: "Billing Care",
    category: "Billing",
  },
];

export function mariaBase(): Omit<Customer360, "contact" | "usage" | "usageStatus" | "verifyChip" | "tickets"> {
  return {
    id: MARIA_ID,
    name: "Maria Delgado",
    accountNumber: MARIA_ID,
    kind: "consumer",
    productsLabel: "Mobile · Internet · Phone · TV",
    customerSince: "2019",
    preferredLanguage: "English",
    standing: "Good standing",
    autoPay: true,
    stats: {
      currentBalance: "$148.72",
      dueMeta: "Due Sep 14 · AutoPay scheduled",
      lastInvoice: "$148.72",
      lastInvoiceMeta: "Up $32.40 vs. Aug",
      activeServices: "6",
      activeServicesMeta: "3 mobile · internet · phone · TV",
      openTickets: "2",
      openTicketsMeta: "1 network · 1 billing",
    },
    health: {
      paymentHistory: "12 of 12 on time (last 12 mo)",
      tenure: "6 yrs 11 mo",
      contract: "Mobile: month-to-month · Internet: ends Mar 2027",
      appEngagement: "Signed in 9× last 30 days",
      churnLabel: "Elevated",
      churnNote: "bill increase + support friction",
    },
    authorizedUsers: [
      { person: "Maria Delgado", role: "Account owner", mayDo: "Everything, including payment methods and PIN" },
      { person: "Luis Delgado", role: "Authorized user", mayDo: "Plan and device changes · no payments or PIN changes" },
    ],
    consent: {
      outageNotifications: "On · text + email",
      marketingContact: "Opted in · text only",
      serviceDataForOffers: "Opted in · Mar 2026",
    },
    timeline: [
      {
        when: "Today 9:41 AM",
        title: "App: viewed bill details",
        detail: "Opened current invoice, spent 3m 20s on charge breakdown, abandoned on “International usage” section.",
      },
      {
        when: "Yesterday 6:12 PM",
        title: "Network: service degradation",
        detail: "Home internet node CHI-114 experienced intermittent outages 5:50–7:30 PM. 212 accounts affected.",
      },
      {
        when: "Sep 2",
        title: "Billing: invoice generated",
        detail: "$148.72 — includes $28.50 international roaming (Mexico, Aug 18–24) and $3.90 regulatory fee change.",
      },
      {
        when: "Aug 26",
        title: "Care: chat interaction",
        detail: "Asked about international plans before travel. No plan added. Ticket INC0048112 (closed).",
      },
    ],
    billing: {
      invoiceTitle: "Current invoice — Sep 2026",
      lines: [
        { label: "Plan charges", amount: "$104.00" },
        { label: "Equipment", amount: "$12.32" },
        { label: "International roaming", amount: "$28.50", flag: "New" },
        { label: "Taxes & fees", amount: "$3.90" },
      ],
      totalLabel: "Total due Sep 14",
      totalAmount: "$148.72",
      paymentMethod: {
        autoPay: "Visa ending 4417 · charges Sep 14",
        wallet: "1 card on file (tokenized)",
      },
      trend: [
        { label: "Apr · $114", amount: 114 },
        { label: "May · $116", amount: 116 },
        { label: "Jun · $114", amount: 114 },
        { label: "Jul · $118", amount: 118 },
        { label: "Aug · $116", amount: 116 },
        { label: "Sep · $149", amount: 149, hot: true },
      ],
      trendNote: "September is 28% above the customer’s 6-month average, driven by international roaming.",
    },
    services: {
      mobile: [
        { line: "(312) 555-0184", plan: "Unlimited Plus", device: "iPhone 16 · paid in full", status: "Active" },
        { line: "(312) 555-0191", plan: "Unlimited Basic", device: "Galaxy S25 · BYOD (customer-owned)", status: "Active" },
        { line: "(312) 555-0207", plan: "Unlimited Basic", device: "Pixel 9 · 6 of 24 mo", status: "Active" },
      ],
      internet: {
        service: "Fiber 500",
        speed: "500/500 Mbps",
        equipment: "Gateway HG-6 · leased",
        status: "Degraded — node CHI-114",
        notice: "Known issue: node CHI-114 intermittent outages under repair. Estimated fix today 4:00 PM. Proactive credit eligibility: yes.",
      },
      phone: {
        service: "Digital Voice",
        number: "(312) 555-0166",
        equipment: "Gateway HG-6 · shared with internet",
        status: "Active",
        note: "Voicemail, caller ID, and spam call blocking included. This address is fiber (FTTH); coax (HFC) addresses run voice and internet through a compatible modem instead.",
      },
      tv: {
        plan: "TV 125+",
        devices: [
          { where: "Living room", device: "4K streaming box (company IP set-top)", status: "Active" },
          { where: "Bedroom", device: "Apple TV · company TV app", status: "Signed in" },
          { where: "On the go", device: "Company TV mobile app · 2 devices", status: "Active" },
        ],
        note: "Fiber addresses use IP video only. Coax (HFC) addresses may also have legacy set-top boxes — see the Harborline business account for an example.",
      },
      addons: [
        { name: "Premium Sports Pack", appliesTo: "TV", price: "$9.99/mo", status: "Active" },
        { name: "Whole-home Wi-Fi", appliesTo: "Internet", price: "$5.00/mo", status: "Active" },
        { name: "Device protection · 2 lines", appliesTo: "Mobile", price: "$18.00/mo", status: "Active" },
        { name: "International travel pass", appliesTo: "Mobile", price: "$10.00/mo", status: "Suggested" },
      ],
    },
    interactions: [
      { when: "Today 10:02 AM", title: "Voice — this interaction", detail: "In progress · Jordan T. · Journal and ticket will attach on wrap-up." },
      { when: "Aug 26 · Chat", title: "International plan question", detail: "Agent: K. Osei · 8 min · Resolved · CSAT 4/5 · Ticket INC0048112" },
      { when: "Jul 12 · App", title: "Plan change (self-serve)", detail: "Line 0207 upgraded to Unlimited Basic · No agent assist" },
      { when: "May 30 · Voice", title: "Payment date question", detail: "Agent: R. Patel · 4 min · Resolved first contact · CSAT 5/5" },
      { when: "Mar 18 · Store", title: "Device upgrade", detail: "Pixel 9 purchased, trade-in applied · Order ORD-88231" },
    ],
    predictions: {
      modelVersion: "ccr-2026.08.2",
      generatedAt: "2026-09-06T10:02:00.000Z",
      items: [
        {
          reason: "billing.unexpectedCharges.roaming",
          label: "Higher bill — roaming charges",
          confidence: 0.86,
          signals: [
            { source: "billing", text: "Bill up $32.40" },
            { source: "appUsage", text: "Viewed charge breakdown 21 min ago" },
            { source: "interaction", text: "Abandoned on roaming section" },
          ],
          suggestedActions: ["explainCharges", "offerTravelPass"],
        },
        {
          reason: "network.outage.followup",
          label: "Internet outage follow-up",
          confidence: 0.54,
          signals: [
            { source: "network", text: "Node CHI-114 degraded yesterday" },
            { source: "account", text: "Open ticket INC0049307" },
            { source: "cohort", text: "38% of affected cohort has called in" },
          ],
          suggestedActions: ["addOutageUpdates"],
        },
        {
          reason: "offers.internationalTravel",
          label: "International plan for future travel",
          confidence: 0.31,
          signals: [
            { source: "engagement", text: "Asked about travel plans Aug 26" },
            { source: "cohort", text: "Similar customers add a travel pass within 30 days" },
          ],
          suggestedActions: ["offerTravelPass"],
        },
      ],
    },
  };
}
